import {Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {TreeNode} from 'primeng/api';
import {MetaService} from '../../../../../core/shared/meta/service/meta.service';
import {MetaDataEntityDto} from '../../../../../core/shared/meta/dto/meta-data-entity.dto';
import {MetaDataAttributeDto} from '../../../../../core/shared/meta/dto/meta-data-attribute.dto';
import {MetaDataJoinDto} from '../../../../../core/shared/meta/dto/meta-data-join.dto';
import {TeCopyPopoverComponent, TeCopyAttrData} from '../te-copy-popover/te-copy-popover.component';
import {DataTypeHelper} from '../../../../../core/shared/helper/data-type.helper';
import {IconClassHelper} from '../../../../../core/shared/helper/icon-class-helper';


// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface PickerAttribute {
    name: string;
    label: string;
    dataType: string;
    path: string;
}

export interface PickerGroup {
    title: string;
    type: 'own' | 'join' | 'nested';
    attrs: PickerAttribute[];
    expanded: boolean;
    prefix: string;
    nestedListExpr?: string;
    nestedItemAlias?: string;
    /** objectTypeId used for lazy-loading join metadata */
    joinObjectTypeId?: number;
    /** Indicates whether this group's attributes have been loaded */
    loaded: boolean;
}

export interface SelectedColumn {
    path: string;
    label: string;
    header: string;
    group: string;
    isNested: boolean;
    nestedListExpr?: string;
    nestedItemAlias?: string;
    cellTemplate: string;
    cellPreset: string;
    dataType?: string;
}

export interface ExistingColumn {
    header: string;
    attrName: string;
    /** Raw cell template HTML from the existing table (e.g., "${item.id}" or "<#if ...>") */
    cellTemplate?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Table Builder Dialog
 *
 * A 3-pane dialog that allows users to visually build FreeMarker-driven HTML
 * tables by picking attributes from entity metadata, reordering columns,
 * customising cell templates, and previewing the generated code.
 *
 * @Input  visible          - Controls dialog visibility (two-way bound).
 * @Input  editMode         - When true the dialog operates in edit mode.
 * @Input  entityAlias      - The entity alias used to resolve metadata.
 * @Input  collectionPath   - The full FreeMarker path to the collection (e.g. "customer.customerContracts").
 * @Input  collectionLabel  - Human-readable label for the collection.
 * @Input  itemAlias        - Loop variable name (e.g. "contract").
 * @Input  existingColumns  - Pre-populated columns when editing an existing table.
 * @Output visibleChange    - Emits when the dialog visibility changes.
 * @Output onInsertTable    - Emits the generated HTML table string.
 * @Output onCancel         - Emits when the user cancels.
 */
@Component({
    selector: 'mvs-te-table-builder-dialog',
    templateUrl: './te-table-builder-dialog.component.html',
    styleUrls: ['./te-table-builder-dialog.component.scss'],
    standalone: false
})
export class TeTableBuilderDialogComponent implements OnInit, OnChanges {

    // -----------------------------------------------------------------------
    // Inputs / Outputs
    // -----------------------------------------------------------------------

    @Input() visible: boolean = false;
    @Input() editMode: boolean = false;
    @Input() entityAlias: string;
    @Input() entityId: number;
    /** Full FreeMarker path to the collection (e.g., "customer.customerContracts") */
    @Input() collectionPath: string;
    @Input() collectionLabel: string;
    @Input() itemAlias: string;
    @Input() existingColumns: ExistingColumn[];

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onInsertTable = new EventEmitter<string>();
    @Output() onCancel = new EventEmitter<void>();

    // -----------------------------------------------------------------------
    // Internal state
    // -----------------------------------------------------------------------

    /** Dialog title derived from mode and collection label */
    dialogTitle: string = 'Tabelle erstellen';

    /** Picker groups shown in the left pane */
    pickerGroups: PickerGroup[] = [];

    /** TreeNode[] representation of pickerGroups for p-tree rendering */
    pickerTreeNodes: TreeNode[] = [];

    /** Ordered list of selected columns (middle pane) */
    selectedColumns: SelectedColumn[] = [];

    /** Index of the column currently being edited in the right pane (-1 = none) */
    activeCellEditorIdx: number = -1;

    /** Convenience getter for the active column */
    get activeColumn(): SelectedColumn | null {
        return this.activeCellEditorIdx >= 0 && this.activeCellEditorIdx < this.selectedColumns.length
            ? this.selectedColumns[this.activeCellEditorIdx]
            : null;
    }

    /** Safe HTML for the table preview */
    tablePreviewHtml: SafeHtml = '';

    /** Raw FreeMarker code preview */
    generatedCode: string = '';

    /** Index being dragged (HTML5 native drag) */
    private dragIdx: number = -1;

    /** Flag indicating metadata is loading */
    busy: boolean = false;

    /** Reference to the copy popover component */
    @ViewChild('copyPopover', {static: false}) copyPopoverRef: TeCopyPopoverComponent;

    // -----------------------------------------------------------------------
    // Constructor
    // -----------------------------------------------------------------------

    constructor(
        private metaService: MetaService,
        private sanitizer: DomSanitizer
    ) {
    }

    // -----------------------------------------------------------------------
    // Lifecycle
    // -----------------------------------------------------------------------

    ngOnInit(): void {
        this.updateDialogTitle();
        if ((this.entityAlias || this.entityId) && this.collectionPath && !this.busy && this.pickerGroups.length === 0) {
            this.buildPickerGroups();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible) {
            this.updateDialogTitle();
            if ((this.entityAlias || this.entityId) && this.collectionPath && !this.busy && this.pickerGroups.length === 0) {
                this.buildPickerGroups();
            }
        }
        if (changes['editMode'] || changes['collectionLabel']) {
            this.updateDialogTitle();
        }
    }

    // -----------------------------------------------------------------------
    // Metadata loading
    // -----------------------------------------------------------------------

    /**
     * Build picker groups from entity metadata.
     * Loads attributes and joins for the collection entity using MetaService.
     */
    buildPickerGroups(): void {
        this.busy = true;
        this.pickerGroups = [];

        // Use entityId (more reliable) if available, otherwise fall back to alias
        const metaObs = this.entityId
            ? this.metaService.getById(this.entityId, true, true, false, false, false)
            : this.metaService.getByAlias(this.entityAlias, true, true, false);

        metaObs.subscribe(
            (meta: MetaDataEntityDto) => {
                // Own attributes group (always expanded)
                const ownAttrs: PickerAttribute[] = (meta.attributes || []).map(attr => ({
                    name: attr.attributeName,
                    label: attr.label || attr.attributeName,
                    dataType: attr.dataType || 'string',
                    path: `${this.itemAlias}.${attr.attributeName}`
                }));

                this.pickerGroups.push({
                    title: 'Eigene Attribute',
                    type: 'own',
                    attrs: ownAttrs,
                    expanded: true,
                    prefix: this.itemAlias,
                    loaded: true
                });

                // Join groups (collapsed, lazy-loaded)
                if (meta.joins) {
                    const sortedJoins = [...meta.joins].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                    for (const join of sortedJoins) {
                        // Backend sends joinRelationEnum as string, not numeric enum index
                        const joinRelation = String(join.joinRelationEnum);
                        if (joinRelation === 'manyToOne' || joinRelation === 'oneToOne') {
                            // ManyToOne / OneToOne joins -> flat access via itemAlias.joinName.attr
                            this.pickerGroups.push({
                                title: join.label || join.name,
                                type: 'join',
                                attrs: [],
                                expanded: false,
                                prefix: `${this.itemAlias}.${join.name}`,
                                joinObjectTypeId: join.joinObjectTypeId,
                                loaded: false
                            });
                        } else if (joinRelation === 'oneToMany') {
                            // OneToMany joins -> nested collection
                            const nestedAlias = join.name.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
                            const shortAlias = nestedAlias.length > 12 ? nestedAlias.substring(0, 12) : nestedAlias;
                            this.pickerGroups.push({
                                title: join.label || join.name,
                                type: 'nested',
                                attrs: [],
                                expanded: false,
                                prefix: shortAlias,
                                joinObjectTypeId: join.joinObjectTypeId,
                                nestedListExpr: `${this.itemAlias}.${join.name}`,
                                nestedItemAlias: shortAlias,
                                loaded: false
                            });
                        }
                    }
                }

                // If we have existing columns for edit mode, pre-populate
                if (this.editMode && this.existingColumns && this.existingColumns.length > 0) {
                    this.prePopulateColumns();
                }

                this.busy = false;
                this.refreshPreview();
                this.pickerTreeNodes = this.buildPickerTreeNodes();
            },
            () => {
                this.busy = false;
            }
        );
    }

    /**
     * Lazy-load attributes for a join/nested group when it is expanded.
     */
    expandGroup(group: PickerGroup): void {
        group.expanded = !group.expanded;

        if (group.expanded && !group.loaded && group.joinObjectTypeId) {
            group.loaded = true; // prevent double-loading
            this.metaService.getById(group.joinObjectTypeId, true, true, false, false, false).subscribe(
                (meta: MetaDataEntityDto) => {
                    group.attrs = (meta.attributes || []).map(attr => ({
                        name: attr.attributeName,
                        label: attr.label || attr.attributeName,
                        dataType: attr.dataType || 'string',
                        path: `${group.prefix}.${attr.attributeName}`
                    }));
                    this.pickerTreeNodes = this.buildPickerTreeNodes();
                },
                () => {
                    // On error, mark as loaded with empty attrs
                    group.attrs = [];
                    this.pickerTreeNodes = this.buildPickerTreeNodes();
                }
            );
        }
    }

    /**
     * Pre-populate selected columns from existingColumns input (edit mode).
     */
    private prePopulateColumns(): void {
        if (!this.existingColumns || this.existingColumns.length === 0) return;

        for (const existing of this.existingColumns) {
            // Try to find the attribute in picker groups by matching the full path
            // or just the attribute name portion.
            // attrName can be "cr_CustomerContract_attr_customer.id" (full path)
            // attr.path is "cr_CustomerContract_attr_customer.id" (itemAlias.attrName)
            let found = false;
            for (const group of this.pickerGroups) {
                const attr = group.attrs.find(a =>
                    a.path === existing.attrName ||
                    a.name === existing.attrName ||
                    // Match by attribute name only (last segment)
                    existing.attrName.endsWith('.' + a.name)
                );
                if (attr) {
                    this.selectedColumns.push({
                        path: attr.path,
                        label: attr.label,
                        header: existing.header || attr.label,
                        group: group.title,
                        isNested: group.type === 'nested',
                        nestedListExpr: group.nestedListExpr,
                        nestedItemAlias: group.nestedItemAlias,
                        cellTemplate: existing.cellTemplate || ('${' + attr.path + '}'),
                        cellPreset: this.detectCellPreset(existing.cellTemplate),
                        dataType: attr.dataType
                    });
                    found = true;
                    break;
                }
            }
            // If not found in loaded groups, create a basic column entry
            if (!found) {
                const path = existing.attrName.includes('.') ? existing.attrName : `${this.itemAlias}.${existing.attrName}`;
                this.selectedColumns.push({
                    path: path,
                    label: existing.attrName.split('.').pop() || existing.attrName,
                    header: existing.header || existing.attrName.split('.').pop() || existing.attrName,
                    group: 'Eigene Attribute',
                    isNested: false,
                    cellTemplate: existing.cellTemplate || ('${' + path + '!"-"}'),
                    cellPreset: this.detectCellPreset(existing.cellTemplate)
                });
            }
        }
    }

    /**
     * Detect the cell preset from a cell template string.
     * Returns 'plain' for simple ${path}, 'ifelse' for null-check patterns,
     * 'bool' for boolean patterns, 'custom' for anything else.
     */
    private detectCellPreset(cellTemplate: string): string {
        if (!cellTemplate) return 'plain';
        const trimmed = cellTemplate.trim();

        // Simple variable: ${path} or ${path!"-"} or ${path!"fallback"}
        if (/^\$\{[^}]+\}$/.test(trimmed)) return 'plain';

        // Null check: <#if path??> ${path} <#else> - </#if>
        if (trimmed.includes('??') && trimmed.includes('<#else>')) return 'ifelse';

        // Boolean: <#if path> Ja <#else> Nein </#if>
        if (trimmed.includes('Ja') && trimmed.includes('Nein') && trimmed.includes('<#if')) return 'bool';

        return 'custom';
    }

    // -----------------------------------------------------------------------
    // Column management
    // -----------------------------------------------------------------------

    /**
     * Check whether the given attribute path is already in the selected columns.
     */
    isColumnSelected(path: string): boolean {
        return this.selectedColumns.some(c => c.path === path);
    }

    /**
     * Add an attribute as a new column.
     */
    addColumn(attr: PickerAttribute, group: PickerGroup): void {
        if (this.isColumnSelected(attr.path)) return;

        this.selectedColumns.push({
            path: attr.path,
            label: attr.label,
            header: attr.label,
            group: group.title,
            isNested: group.type === 'nested',
            nestedListExpr: group.nestedListExpr,
            nestedItemAlias: group.nestedItemAlias,
            cellTemplate: '${' + attr.path + '!"-"}',
            cellPreset: 'plain',
            dataType: attr.dataType
        });
        this.refreshPreview();
    }

    /**
     * Remove a column by index.
     */
    removeColumn(idx: number): void {
        this.selectedColumns.splice(idx, 1);
        if (this.activeCellEditorIdx === idx) {
            this.activeCellEditorIdx = -1;
        } else if (this.activeCellEditorIdx > idx) {
            this.activeCellEditorIdx--;
        }
        this.refreshPreview();
    }

    /**
     * Move a column up (-1) or down (+1).
     */
    moveColumn(idx: number, direction: number): void {
        const target = idx + direction;
        if (target < 0 || target >= this.selectedColumns.length) return;

        const temp = this.selectedColumns[idx];
        this.selectedColumns[idx] = this.selectedColumns[target];
        this.selectedColumns[target] = temp;

        // Update active editor index if it was one of the swapped items
        if (this.activeCellEditorIdx === idx) {
            this.activeCellEditorIdx = target;
        } else if (this.activeCellEditorIdx === target) {
            this.activeCellEditorIdx = idx;
        }

        this.refreshPreview();
    }

    /**
     * Select a column for editing in the right pane.
     */
    selectColumnForEdit(idx: number): void {
        this.activeCellEditorIdx = idx;
    }

    /**
     * Check whether a column has a custom (non-plain) template.
     */
    hasCustomTemplate(col: SelectedColumn): boolean {
        return col.cellPreset !== 'plain';
    }

    /**
     * Get the data type of a column for conditional preset display.
     */
    getColumnDataType(col: SelectedColumn): string {
        return col.dataType || 'string';
    }

    /**
     * Get the icon class for a selected column based on its data type.
     */
    getColumnIcon(col: SelectedColumn): string {
        if (col.isNested) return 'fa-regular fa-layer-group text-orange-500';
        if (!col.dataType) return 'fa-regular fa-cube text-blue-500';
        const internalType = DataTypeHelper.getInternalDataType(col.dataType);
        return IconClassHelper.getIconClass(internalType);
    }

    // -----------------------------------------------------------------------
    // Drag and drop (HTML5 native)
    // -----------------------------------------------------------------------

    dragStart(event: DragEvent, idx: number): void {
        this.dragIdx = idx;
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
        }
    }

    dragOver(event: DragEvent): void {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
    }

    dragDrop(event: DragEvent, targetIdx: number): void {
        event.preventDefault();
        if (this.dragIdx < 0 || this.dragIdx === targetIdx) return;

        const item = this.selectedColumns.splice(this.dragIdx, 1)[0];
        this.selectedColumns.splice(targetIdx, 0, item);

        // Update active editor index
        if (this.activeCellEditorIdx === this.dragIdx) {
            this.activeCellEditorIdx = targetIdx;
        } else if (this.dragIdx < this.activeCellEditorIdx && targetIdx >= this.activeCellEditorIdx) {
            this.activeCellEditorIdx--;
        } else if (this.dragIdx > this.activeCellEditorIdx && targetIdx <= this.activeCellEditorIdx) {
            this.activeCellEditorIdx++;
        }

        this.dragIdx = -1;
        this.refreshPreview();
    }

    dragLeave(event: DragEvent): void {
        // No-op, kept for template binding
    }

    // -----------------------------------------------------------------------
    // Cell template presets
    // -----------------------------------------------------------------------

    /**
     * Apply a cell template preset to the active column.
     */
    applyCellPreset(preset: string): void {
        const col = this.activeColumn;
        if (!col) return;

        const p = col.path;
        col.cellPreset = preset;

        switch (preset) {
            case 'plain':
                col.cellTemplate = '${' + p + '!"-"}';
                break;
            case 'ifelse':
                col.cellTemplate = '<#if ' + p + '??>\n  ${' + p + '}\n<#else>\n  -\n</#if>';
                break;
            case 'bool':
                col.cellTemplate = '<#if ' + p + '>\n  Ja\n<#else>\n  Nein\n</#if>';
                break;
            case 'custom':
                col.cellTemplate = '';
                break;
            default:
                col.cellTemplate = '${' + p + '!"-"}';
                break;
        }

        this.refreshPreview();
    }

    /**
     * Called when the cell template textarea changes.
     */
    onCellTemplateChange(): void {
        if (this.activeColumn) {
            this.activeColumn.cellPreset = 'custom';
        }
        this.refreshPreview();
    }

    // -----------------------------------------------------------------------
    // Code generation
    // -----------------------------------------------------------------------

    /**
     * Generate the final HTML table with the list loop INSIDE <tbody>.
     *
     * Structure produced (simple case — no nested collections):
     *
     *   WYSIWYG (data attributes on table):
     *     <table data-te-null-guard="rootAlias" ...>
     *       <thead><tr><th>...</th></tr></thead>
     *       <tbody>
     *         <tr data-te-loop-inner="collectionPath" data-te-loop-inner-alias="itemAlias">
     *           <td>...</td>
     *         </tr>
     *       </tbody>
     *     </table>
     *
     *   FreeMarker output (after toF conversion):
     *     <#if rootAlias??>
     *     <table ...>
     *       <thead><tr><th>...</th></tr></thead>
     *       <tbody>
     *         <#list collectionPath as itemAlias>
     *         <tr><td>...</td></tr>
     *         </#list>
     *       </tbody>
     *     </table>
     *     </#if>
     *
     * data-te-null-guard on <table> is converted by the TinyMCE plugin's toF
     * to <#if rootAlias??> ... </#if> around the table. This avoids TinyMCE
     * auto-wrapping raw <#if> in <p> tags.
     *
     * data-te-loop-inner on <tr> is converted to <#list>...</#list> wrapping
     * only the row — so N items produce N rows in ONE table.
     */
    generateTableHtml(): string {
        const cols = this.selectedColumns;
        if (cols.length === 0) return '';

        const flatCols = cols.filter(c => !c.isNested);
        const nestedCols = cols.filter(c => c.isNested);

        // Group nested cols by their list expression
        const nestedGroups: { [key: string]: { listExpr: string; itemAlias: string; cols: SelectedColumn[] } } = {};
        nestedCols.forEach(c => {
            const key = `${c.nestedListExpr}|${c.nestedItemAlias}`;
            if (!nestedGroups[key]) {
                nestedGroups[key] = {listExpr: c.nestedListExpr, itemAlias: c.nestedItemAlias, cols: []};
            }
            nestedGroups[key].cols.push(c);
        });

        // Extract root variable alias for the null guard.
        // collectionPath is e.g. "customer.cr_CustomerContract_attr_customer"
        // rootAlias is the first segment: "customer"
        const rootAlias = this.collectionPath.split('.')[0];

        // data-te-null-guard stores the root alias; the TinyMCE plugin's toF
        // converts it to <#if rootAlias??> ... </#if> around the table.
        // Using a data attribute avoids TinyMCE wrapping raw <#if> in <p> tags.
        // data-te-entity-alias and data-te-entity-id are stored so double-click
        // edit can re-resolve the entity metadata for the picker groups.
        let html = `<table data-te-null-guard="${rootAlias}" data-te-entity-alias="${this.entityAlias || ''}" data-te-entity-id="${this.entityId || 0}" border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse;width:100%">`;

        // Header row (outside the loop — rendered once)
        html += '<thead><tr>';
        cols.forEach(c => {
            html += `<th>${c.header}</th>`;
        });
        html += '</tr></thead><tbody>';

        // Body rows — the main collection loop wraps only the <tr>
        if (Object.keys(nestedGroups).length === 0) {
            // Simple case: no nested collections
            // data-te-loop-inner on <tr> tells the TinyMCE plugin to convert
            // this into <#list collectionPath as itemAlias><tr>...</tr></#list>
            html += `<tr data-te-loop-inner="${this.collectionPath}" data-te-loop-inner-alias="${this.itemAlias}">`;
            flatCols.forEach(c => {
                html += `<td>${c.cellTemplate}</td>`;
            });
            html += '</tr>';
        } else {
            // Mixed: flat + nested columns — each nested group gets its own looped <tr>
            Object.values(nestedGroups).forEach(ng => {
                html += `<tr data-te-loop-inner="${ng.listExpr}" data-te-loop-inner-alias="${ng.itemAlias}">`;
                flatCols.forEach(c => {
                    html += `<td>${c.cellTemplate}</td>`;
                });
                ng.cols.forEach(c => {
                    html += `<td>${c.cellTemplate}</td>`;
                });
                html += '</tr>';
            });
        }

        html += '</tbody></table>';

        return html;
    }

    /**
     * Refresh the preview panes (table preview + FreeMarker code).
     */
    refreshPreview(): void {
        this.generatedCode = this.generateTableHtml();

        // Build a safe HTML preview
        if (this.selectedColumns.length > 0) {
            let previewHtml = '<table border="1" cellpadding="4" cellspacing="0" style="border-collapse:collapse;width:100%;font-size:12px;">';
            previewHtml += '<thead><tr>';
            this.selectedColumns.forEach(c => {
                previewHtml += `<th style="background:#f0f0f0;padding:4px 8px;">${this.escapeHtml(c.header)}</th>`;
            });
            previewHtml += '</tr></thead><tbody><tr>';
            this.selectedColumns.forEach(c => {
                previewHtml += `<td style="padding:4px 8px;"><code>${this.escapeHtml(c.cellTemplate)}</code></td>`;
            });
            previewHtml += '</tr></tbody></table>';

            this.tablePreviewHtml = this.sanitizer.bypassSecurityTrustHtml(previewHtml);
        } else {
            this.tablePreviewHtml = '';
        }
    }

    // -----------------------------------------------------------------------
    // Dialog actions
    // -----------------------------------------------------------------------

    /**
     * Handle the insert/update button click.
     */
    handleInsert(): void {
        const html = this.generateTableHtml();
        this.onInsertTable.emit(html);
        this.visible = false;
        this.visibleChange.emit(false);
    }

    /**
     * Handle the cancel button or dialog close.
     */
    handleCancel(): void {
        this.onCancel.emit();
        this.visible = false;
        this.visibleChange.emit(false);
    }

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    private updateDialogTitle(): void {
        const label = this.collectionLabel || this.collectionPath || 'Tabelle';
        this.dialogTitle = this.editMode
            ? `Tabelle bearbeiten: ${label}`
            : `Tabelle erstellen: ${label}`;
    }

    private escapeHtml(text: string): string {
        if (!text) return '';
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    // -----------------------------------------------------------------------
    // P-Tree adapter
    // -----------------------------------------------------------------------

    /**
     * Convert pickerGroups to TreeNode[] for p-tree rendering.
     * Called after buildPickerGroups() and expandGroup() complete.
     */
    buildPickerTreeNodes(): TreeNode[] {
        return this.pickerGroups.map(group => {
            const iconClass = group.type === 'own' ? 'fa-regular fa-cube text-blue-500'
                : group.type === 'join' ? 'fa-regular fa-link text-cyan-600'
                : 'fa-regular fa-layer-group text-orange-500';
            return {
                label: group.title,
                type: group.type === 'own' ? 'object-root' : group.type === 'join' ? 'join' : 'collection',
                expandedIcon: iconClass,
                collapsedIcon: iconClass,
                expanded: group.expanded,
                leaf: false,
                data: {groupRef: group},
                children: group.loaded ? group.attrs.map(attr => {
                    const internalType = DataTypeHelper.getInternalDataType(attr.dataType);
                    const attrIcon = IconClassHelper.getIconClass(internalType);
                    return {
                        label: attr.label,
                        type: 'attribute',
                        collapsedIcon: attrIcon,
                        leaf: true,
                        data: {attrRef: attr, groupRef: group, path: attr.path, dataType: attr.dataType}
                    };
                }) : []
            };
        });
    }

    /**
     * Handle p-tree node expand — delegates to existing expandGroup().
     */
    handlePickerNodeExpand(event: any): void {
        const group = event.node?.data?.groupRef as PickerGroup;
        if (group && !group.loaded) {
            // expandGroup toggles expanded and loads metadata;
            // but p-tree already set expanded=true, so we need to ensure group.expanded stays true.
            // expandGroup() toggles it, so set it to false first so the toggle sets it to true.
            group.expanded = false;
            this.expandGroup(group);
        }
    }

    // -----------------------------------------------------------------------
    // Copy popover
    // -----------------------------------------------------------------------

    /**
     * Open the copy popover with context-aware options for the given attribute.
     * Provides the same copy functionality as the te-variables p-tree.
     */
    handleCopyPopover(attr: PickerAttribute, group: PickerGroup, event: MouseEvent): void {
        event.stopPropagation();

        let type: 'attribute' | 'collection' | 'join' = 'attribute';
        if (group.type === 'nested') {
            type = 'collection';
        } else if (group.type === 'join') {
            type = 'join';
        }

        const attrData: TeCopyAttrData = {
            path: attr.path,
            label: attr.label,
            type: type,
            dataType: this.mapDataType(attr.dataType),
            itemAlias: group.nestedItemAlias
        };

        if (this.copyPopoverRef) {
            this.copyPopoverRef.show(event, attrData);
        }
    }

    /**
     * Map metadata data type strings to simple type categories
     * used by the copy popover for context-aware options.
     */
    private mapDataType(dataType: string): string {
        if (!dataType) return 'string';
        const dt = dataType.toLowerCase();
        if (dt === 'boolean' || dt === 'bool') return 'boolean';
        if (dt.includes('enum')) return 'enum';
        if (dt.includes('date') || dt.includes('time') || dt.includes('timestamp')) return 'date';
        if (dt.includes('decimal') || dt.includes('currency') || dt.includes('money') || dt.includes('bigdecimal')) return 'currency';
        if (dt.includes('int') || dt.includes('long') || dt.includes('number') || dt.includes('double') || dt.includes('float')) return 'number';
        return 'string';
    }
}
